import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Household, Place } from '@pacer/shared';
import * as api from '../../api';
import { Icon, weatherIcon } from '../../components/Icon';
import { Button, Card, Field, Input, cx } from '../../components/ui';
import { queryKeys } from '../../state/queries';

/** Search by city name — nobody should have to look up their own latitude. */
export function WeatherPanel({ household }: { household: Household | undefined }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Place[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: weather } = useQuery({ queryKey: queryKeys.weather, queryFn: api.getWeather });

  const search = useMutation({
    mutationFn: () => api.searchPlaces(q.trim()),
    onSuccess: (places) => {
      setResults(places);
      setError(places.length === 0 ? 'No matches. Try a nearby larger town.' : null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.household });
    await qc.invalidateQueries({ queryKey: queryKeys.weather });
  };

  const choose = useMutation({
    mutationFn: (p: Place) =>
      api.updateHousehold({
        weatherLat: p.latitude,
        weatherLon: p.longitude,
        weatherPlace: [p.name, p.admin].filter(Boolean).join(', '),
      }),
    onSuccess: async () => {
      setResults(null);
      setQ('');
      await refresh();
    },
  });

  const clear = useMutation({
    mutationFn: () => api.updateHousehold({ weatherLat: null, weatherLon: null, weatherPlace: null }),
    onSuccess: refresh,
  });

  const setUnit = useMutation({
    mutationFn: (unit: 'f' | 'c') => api.updateHousehold({ weatherUnit: unit }),
    onSuccess: refresh,
  });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-[20px] text-primary">Weather</h2>

      <Card className="flex flex-col gap-4 p-4">
        {household?.weatherPlace ? (
          <div className="flex items-center gap-3">
            {weather && (
              <Icon
                name={weatherIcon(weather.now.code, weather.now.isDay)}
                size={28}
                strokeWidth={1.2}
                className="text-accent"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] text-primary">{household.weatherPlace}</div>
              <div className="tabular font-mono text-[12px] text-muted">
                {weather ? `${weather.now.tempF}° · ${weather.now.label}` : 'Fetching…'}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => clear.mutate()}>
              Remove
            </Button>
          </div>
        ) : (
          <p className="font-mono text-[12px] text-muted">
            No location set. The weather card and screensaver stay hidden until there is one.
          </p>
        )}

        <Field label="Find your town">
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && q.trim().length >= 2 && search.mutate()}
              placeholder="Boston"
              autoCapitalize="words"
              enterKeyHint="search"
            />
            <Button
              variant="secondary"
              onClick={() => search.mutate()}
              disabled={q.trim().length < 2 || search.isPending}
              className="shrink-0"
            >
              {search.isPending ? 'Searching…' : 'Search'}
            </Button>
          </div>
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}

        {results && results.length > 0 && (
          <ul className="flex flex-col overflow-hidden rounded-md border border-border-subtle">
            {results.map((p) => (
              <li key={`${p.latitude},${p.longitude}`} className="border-b border-border-subtle last:border-b-0">
                <button
                  onClick={() => choose.mutate(p)}
                  className="touch flex w-full items-center gap-2 px-3 text-left transition-colors hover:bg-surface-high"
                >
                  <span className="flex-1 text-[14px] text-primary">
                    {p.name}
                    <span className="text-muted">
                      {[p.admin, p.country].filter(Boolean).length > 0 &&
                        ` · ${[p.admin, p.country].filter(Boolean).join(', ')}`}
                    </span>
                  </span>
                  <Icon name="chevron-right" size={13} className="text-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <Field label="Units">
          <div className="inline-flex items-center gap-0.5 rounded-md border border-border-subtle bg-surface p-0.5">
            {(['f', 'c'] as const).map((u) => (
              <button
                key={u}
                onClick={() => setUnit.mutate(u)}
                className={cx(
                  'h-9 rounded-[5px] px-4 font-mono text-[13px] transition-colors',
                  household?.weatherUnit === u
                    ? 'bg-[var(--color-accent-dim)] text-accent'
                    : 'text-muted hover:bg-surface-high hover:text-secondary',
                )}
              >
                °{u.toUpperCase()}
              </button>
            ))}
          </div>
        </Field>

        <p className="font-mono text-[12px] leading-relaxed text-muted">
          Forecast from Open-Meteo. No API key, no account — nothing here can expire and quietly
          break the wall.
        </p>
      </Card>
    </section>
  );
}
